import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useConnectSeamVisionlineUser } from '@/api/keyless/seam-visionline/connect-seam-visionline-user'
import { useCreateSeamVisionlineUser } from '@/api/keyless/seam-visionline/create-seam-visionline-user'
import { useGetSeamVisionlineDisconnectedUsers } from '@/api/keyless/seam-visionline/get-seam-visionline-disconnected-users'
import { useGetSeamVisionlineEntrances } from '@/api/keyless/seam-visionline/get-seam-visionline-entrances'
import { useGetSeamVisionlineIsConnected } from '@/api/keyless/seam-visionline/get-seam-visionline-is-connected'
import { useGetSeamVisionlineUser } from '@/api/keyless/seam-visionline/get-seam-visionline-user'
import { useRevokeSeamVisionlineEndpoint } from '@/api/keyless/seam-visionline/revoke-seam-visionline-endpoint'
import { useUpdateSeamVisionlineUser } from '@/api/keyless/seam-visionline/update-seam-visionline-user'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ChipSelect } from '@/components/ui/chip-select'
import { Collapse } from '@/components/ui/collapse'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { Flex } from '@/components/ui/flex'
import { Select } from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { API_ERRORS } from '@/constants'
import { EMPTY_VALUE } from '@/constants/ui'
import { popupFactory, toastFactory } from '@/factories'
import { getApiErrorMessage, getApiErrorStatus } from '@/utils/error'

import * as S from '../keyless-access.styled'
import { computeKeylessArrayDiff } from '../keyless-access.utils'
import { KeylessNotConnected } from '../keyless-access-not-connected'
import { KeylessTabSkeleton } from '../keyless-access-tab-skeleton'
import { SeamVisionlineTabProps } from './seam-visionline-tab.types'

export const SeamVisionlineTab = ({
  clientId,
  buildingId,
  clientApartment,
  canEdit,
  isEditing,
  onEditingChange,
  onRegisterSave,
  onRegisterGetChanges,
  onLoadingChange,
  onConnectionChange,
}: SeamVisionlineTabProps) => {
  const { t } = useTranslation(['clients', 'common'])
  const [selectedEntranceIds, setSelectedEntranceIds] = useState<string[] | null>(null)
  const [showDisconnectedList, setShowDisconnectedList] = useState(false)
  const [selectedConnectUserId, setSelectedConnectUserId] = useState<string | null>(null)

  const {
    data: isConnected,
    isLoading,
    refetch: refetchConnected,
  } = useGetSeamVisionlineIsConnected({
    clientId,
  })
  const {
    data: visionlineUser,
    isFetching: isFetchingUser,
    refetch: refetchUser,
  } = useGetSeamVisionlineUser({ clientId }, Boolean(isConnected))
  const { data: entrances = [] } = useGetSeamVisionlineEntrances({ buildingId })
  const {
    data: disconnectedUsers = [],
    isFetching: isFetchingDisconnected,
    refetch: refetchDisconnected,
  } = useGetSeamVisionlineDisconnectedUsers({ buildingId }, showDisconnectedList)

  const { mutateAsync: createUser, isPending: isCreating } = useCreateSeamVisionlineUser()
  const { mutateAsync: connectUser } = useConnectSeamVisionlineUser()
  const { mutateAsync: updateUser } = useUpdateSeamVisionlineUser()
  const { mutateAsync: revokeEndpoint, isPending: isRevoking } = useRevokeSeamVisionlineEndpoint()

  const handleSaveEntranceRef = useRef<() => Promise<void>>(undefined)

  const handleOpenSaveConfirm = () =>
    popupFactory.open(ConfirmationPopup, {
      children: t('clients:keylessAccess.seamVisionline.confirmSave.confirmBody'),
      confirmText: t('clients:keylessAccess.seamVisionline.confirmSave.confirmText'),
      onConfirm: () => handleSaveEntranceRef.current?.(),
      title: t('clients:keylessAccess.seamVisionline.confirmSave.confirmTitle'),
      variant: 'warning',
    })

  useEffect(() => {
    if (!isEditing) return
    onRegisterSave?.(handleOpenSaveConfirm)
    return () => onRegisterSave?.(null)
  }, [isEditing, onRegisterSave])

  const currentValuesRef = useRef({ entrances, selectedEntranceIds, visionlineUser })
  currentValuesRef.current = { entrances, selectedEntranceIds, visionlineUser }
  const initialEntranceIdsRef = useRef<string[]>([])

  useEffect(() => {
    if (!isEditing) {
      onRegisterGetChanges?.(null)
      return
    }
    initialEntranceIdsRef.current = (currentValuesRef.current.visionlineUser?.entrances ?? []).map(
      (e) => e.id,
    )
    onRegisterGetChanges?.(() => {
      const {
        entrances: ents,
        selectedEntranceIds: sel,
        visionlineUser: user,
      } = currentValuesRef.current
      const initial = initialEntranceIdsRef.current
      const current = sel ?? (user?.entrances ?? []).map((e) => e.id)
      const diff = computeKeylessArrayDiff(
        initial,
        current,
        'Door',
        (id) => ents.find((e) => e.id === id)?.name ?? id,
      )
      return diff ? [diff] : []
    })
    return () => onRegisterGetChanges?.(null)
  }, [isEditing, onRegisterGetChanges])

  const isTabLoading = isLoading || isFetchingUser

  useEffect(() => {
    onLoadingChange?.(isTabLoading)
    return () => onLoadingChange?.(false)
  }, [isTabLoading, onLoadingChange])

  useEffect(() => {
    if (isLoading) return
    onConnectionChange?.(Boolean(isConnected))
  }, [isConnected, isLoading, onConnectionChange])

  if (isTabLoading) return <KeylessTabSkeleton rowCount={1} />

  const activeEntranceIds =
    selectedEntranceIds ?? (visionlineUser?.entrances ?? []).map((e) => e.id)

  const handleCreateNewUser = async () => {
    try {
      await createUser({ clientId })
      const matchedEntrance = entrances.find((e) => e.name === String(clientApartment))
      await updateUser({ clientId, entranceIds: matchedEntrance ? [matchedEntrance.id] : [] })
      toastFactory.success(t('clients:keylessAccess.seamVisionline.toasts.createSuccess'))
      void refetchConnected()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleFetchConnectList = async () => {
    const result = await refetchDisconnected()
    const users = result.data ?? []
    if (users.length === 0) {
      toastFactory.error(t('clients:keylessAccess.seamVisionline.toasts.noAccountsToConnect'))
    } else {
      setShowDisconnectedList(true)
    }
  }

  const handleConnectConfirmed = async () => {
    if (!selectedConnectUserId) return
    try {
      await connectUser({ clientId, seamUserId: selectedConnectUserId })
      toastFactory.success(t('clients:keylessAccess.seamVisionline.toasts.connectSuccess'))
      setShowDisconnectedList(false)
      void refetchConnected()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleSaveEntrance = async () => {
    try {
      await updateUser({ clientId, entranceIds: activeEntranceIds })
      toastFactory.success(t('clients:keylessAccess.seamVisionline.toasts.entranceUpdateSuccess'))
      void refetchUser()
      onEditingChange(false)
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }
  handleSaveEntranceRef.current = handleSaveEntrance

  const handleRevoke = async () => {
    try {
      await revokeEndpoint({ clientId })
      toastFactory.success(t('clients:keylessAccess.seamVisionline.revoke.toasts.success'))
      void refetchConnected()
      void refetchUser()
    } catch (error) {
      const status = getApiErrorStatus(error as Error)
      if (status === API_ERRORS.FAILED_DEPENDENCY) {
        toastFactory.error(t('clients:keylessAccess.seamVisionline.revoke.toasts.accessError'))
        return
      }
      if (status === API_ERRORS.SERVICE_UNAVAILABLE) {
        // The keys are already off the doors, only the VisionLine container survived - refetch so
        // the block reflects whatever the partial revoke left behind, and keep the button around.
        toastFactory.error(t('clients:keylessAccess.seamVisionline.revoke.toasts.connectionError'))
        void refetchConnected()
        void refetchUser()
        return
      }
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleOpenRevokeConfirm = () => {
    popupFactory.open(ConfirmationPopup, {
      children: t('clients:keylessAccess.seamVisionline.revoke.confirmBody'),
      confirmText: t('clients:keylessAccess.seamVisionline.revoke.confirmText'),
      onConfirm: handleRevoke,
      title: t('clients:keylessAccess.seamVisionline.revoke.confirmTitle'),
      variant: 'danger',
    })
  }

  const handleOpenCreateConfirm = () => {
    setShowDisconnectedList(false)
    setSelectedConnectUserId(null)
    popupFactory.open(ConfirmationPopup, {
      children: t('clients:keylessAccess.seamVisionline.createUser.confirmBody'),
      confirmText: t('clients:keylessAccess.seamVisionline.createUser.confirmText'),
      onConfirm: handleCreateNewUser,
      title: t('clients:keylessAccess.seamVisionline.createUser.confirmTitle'),
      variant: 'warning',
    })
  }

  const handleOpenConnectConfirm = () => {
    popupFactory.open(ConfirmationPopup, {
      children: t('clients:keylessAccess.seamVisionline.connectUserConfirmation.confirmBody'),
      confirmText: t('clients:keylessAccess.seamVisionline.connectUserConfirmation.confirmText'),
      onConfirm: handleConnectConfirmed,
      title: t('clients:keylessAccess.seamVisionline.connectUserConfirmation.confirmTitle'),
      variant: 'warning',
    })
  }

  if (!isConnected) {
    const connectOptions = disconnectedUsers.map((u) => ({
      label: u.name || u.email || u.id,
      value: u.id,
    }))
    return (
      <S.TabContent>
        <KeylessNotConnected
          isConnectHidden
          label={t('clients:keylessAccess.seamVisionline.label')}
          isCreating={isCreating}
          isConnecting={isFetchingDisconnected}
          isConnectDisabled={showDisconnectedList}
          onCreateNew={handleOpenCreateConfirm}
          onConnectExisting={() => void handleFetchConnectList()}
        />
        <Collapse in={showDisconnectedList} animation="grow">
          <S.FieldRow>
            <Flex gap={6} align="center">
              <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                {t('clients:keylessAccess.seamVisionline.selectUser.label')}
              </Typography>
            </Flex>
            <Flex gap={12} justify="flex-end" align="center">
              <Select
                size="small"
                options={connectOptions}
                value={selectedConnectUserId}
                onChange={(value: string) => setSelectedConnectUserId(value)}
                onReset={() => setSelectedConnectUserId(null)}
              />
              <Button
                size="small"
                disabled={!selectedConnectUserId}
                onClick={handleOpenConnectConfirm}
              >
                {t('common:actions.confirm')}
              </Button>
            </Flex>
          </S.FieldRow>
        </Collapse>
      </S.TabContent>
    )
  }

  const entranceOptions = entrances.map((e) => ({ label: e.name, value: e.id }))

  if (!isEditing) {
    const currentEntrances = visionlineUser?.entrances ?? []
    return (
      <S.TabContent>
        <S.FieldRow>
          <Flex gap={6} align="center">
            <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
              {t('clients:keylessAccess.seamVisionline.entrance.label')}
            </Typography>
          </Flex>
          <S.FieldValue>
            {currentEntrances.length > 0 ? (
              <Flex gap={6} wrap="wrap">
                {currentEntrances.map((e) => (
                  <Badge key={e.id} color="blue" size="small" variant="light">
                    {e.name}
                  </Badge>
                ))}
              </Flex>
            ) : (
              EMPTY_VALUE
            )}
          </S.FieldValue>
        </S.FieldRow>
        {canEdit && (
          <S.FieldRow>
            <span />
            <Flex justify="flex-end">
              <Button
                size="small"
                use="danger"
                loading={isRevoking}
                onClick={handleOpenRevokeConfirm}
              >
                {t('clients:keylessAccess.seamVisionline.revoke.action')}
              </Button>
            </Flex>
          </S.FieldRow>
        )}
      </S.TabContent>
    )
  }

  return (
    <S.TabContent>
      <S.FieldRow>
        <Flex gap={6} align="center">
          <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
            {t('clients:keylessAccess.seamVisionline.entrance.label')}
          </Typography>
        </Flex>
        <ChipSelect
          size="small"
          options={entranceOptions}
          value={activeEntranceIds}
          placeholder={t('clients:keylessAccess.seamVisionline.entrance.placeholder')}
          onChange={setSelectedEntranceIds}
        />
      </S.FieldRow>
    </S.TabContent>
  )
}
